/**
 * Multi-language related operations
 */
import type { LocaleType } from '/#/config'

import { i18n } from './setupI18n'
import { useLocaleStoreWithOut } from '/@/store/modules/locale'
import { unref, computed } from 'vue'
import { loadLocalePool, setHtmlPageLang } from './helper'

interface LangModule {
  message: Recordable
  dateLocale: Recordable
  dateLocaleName: string
}

function setI18nLanguage(locale: LocaleType) {
  const localeStore = useLocaleStoreWithOut()

  if (i18n.mode === 'legacy') {
    i18n.global.locale = locale
  } else {
    ;(i18n.global.locale as any).value = locale
  }
  localeStore.setLocaleInfo({ locale })
  setHtmlPageLang(locale)
}

export function useLocale() {
  const localeStore = useLocaleStoreWithOut()
  const getLocale = computed(() => localeStore.getLocale)
  const getShowLocalePicker = computed(() => localeStore.getShowPicker)

  const getAntdLocale = computed((): any => {
    return i18n.global.getLocaleMessage(unref(getLocale))?.antdLocale ?? {}
  })

  const changeUrl = (locale: LocaleType) => {
    const currentPath = location.href
    const oldLocale = getLocale.value

    // 如果当前路径包含旧的语言代码，进行替换
    if (currentPath.includes(`/${oldLocale}`)) {
      const newPath = currentPath.replace(`/${oldLocale}`, `/${locale}`)
      console.log(newPath)
      window.history.pushState(null, '', newPath)
    } else {
      // 如果当前路径不包含语言代码，保持路径不变
      // 或者可以根据需要在路径前添加语言代码
      console.log('Current path does not contain locale code, skipping URL update')
    }
  }

  // Switching the language will change the locale of useI18n
  // And submit to configuration modification
  async function changeLocale(locale: LocaleType, isChangeUrl = true) {
    const globalI18n = i18n.global
    const currentLocale = unref(globalI18n.locale)
    if (currentLocale === locale) {
      return locale
    }
    if (isChangeUrl) {
      changeUrl(locale)
    }
    if (loadLocalePool.includes(locale)) {
      setI18nLanguage(locale)
      return locale
    }
    const langModule = ((await import(`./lang/${locale}.ts`)) as any).default as LangModule
    if (!langModule) return

    const { message } = langModule

    globalI18n.setLocaleMessage(locale, message)
    loadLocalePool.push(locale)

    setI18nLanguage(locale)
    return locale
  }

  return {
    getLocale,
    getShowLocalePicker,
    changeLocale,
    getAntdLocale,
  }
}
